import type { WizardStep } from "@sentricmusic/ui";

import { Form, WizardProgress } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";

import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import useGigsClaimTour from "@/components/tour/GigsClaimTour";

import AdmissionSection from "./components/AdmissionSection";
import EvidenceSection from "./components/EvidenceSection";
import FestivalSection from "./components/FestivalSection";
import GigDetailsSection from "./components/GigDetailsSection";
import GigsClaimActions from "./components/GigsClaimActions";
import GigsClaimAlerts from "./components/GigsClaimAlerts";
import GigsClaimHeader from "./components/GigsClaimHeader";
import LineUpSection from "./components/LineUpSection";
import OtherPerformersSection from "./components/OtherPerformersSection";
import PromoterSection from "./components/PromoterSection";
import SpainProofSection from "./components/SpainProofSection";
import VenueSection from "./components/VenueSection";
import useGigsClaimForm from "./hooks/useGigsClaimForm";
import useGigsClaimMutation from "./hooks/useGigsClaimMutation";

function GigsClaim() {
  const navigate = useNavigate();
  const { t } = useTranslation("gigs");
  const { data: siteConfig } = useSiteConfigAuth();
  if (siteConfig?.disable_gigs) {
    void navigate({ to: "/" });
  }

  const flagsDarkly = useFlags();
  const showTourFlag = flagsDarkly["pd-show-tutorial"] as boolean;
  const startTour = useGigsClaimTour();
  const startGigsClaimTour = () => startTour(true);

  const {
    form,
    id,
    isUpdate,
    showManual,
    setShowManual,
    sections,
    festival,
    otherPerformers,
    underTwelveEuros,
    isUsAlertOpen,
    setIsUsAlertOpen,
    isDenmarkAlertOpen,
    setIsDenmarkAlertOpen,
    adminRights,
    myAccount,
    setlists,
    gigClaims,
    refetchGigClaim,
  } = useGigsClaimForm();

  const {
    pending,
    mutationErrorMessage,
    handleEvidenceChange,
    addClaim,
    updateClaim,
    deleteClaim,
  } = useGigsClaimMutation({
    form,
    id,
    gigClaims,
    refetchGigClaim,
    myAccount,
    setlists,
  });

  const country = form.watch("country");
  const countrySet = country != null;
  const setlist = form.watch("setlist");
  const detailsComplete = isUpdate || Boolean(setlist);
  const claimStepsMobile: WizardStep[] = [
    { id: "venue", title: t("gigs:step-label-venue", "Venue") },
    { id: "details", title: t("gigs:step-label-details", "Gig Details") },
    { id: "submit", title: t("gigs:step-label-submit", "Submit") },
  ];
  const claimStepsDesktop: WizardStep[] = countrySet
    ? [
        { id: "venue", title: t("gigs:step-label-venue", "Venue") },
        { id: "details", title: t("gigs:step-label-details", "Gig Details") },
        { id: "submit", title: t("gigs:step-label-submit", "Submit") },
      ]
    : [{ id: "venue", title: t("gigs:step-label-venue", "Venue") }];
  const currentStepIndex = detailsComplete ? 2 : countrySet ? 1 : 0;
  return (
    <Form {...form}>
      <>
        <GigsClaimAlerts
          isDenmarkAlertOpen={isDenmarkAlertOpen}
          setIsDenmarkAlertOpen={setIsDenmarkAlertOpen}
          isUsAlertOpen={isUsAlertOpen}
          setIsUsAlertOpen={setIsUsAlertOpen}
        />

        <div
          className="flex-1 order-last xl:w-full bg-layer-raised min-h-50 lg:min-h-screen-minus-header"
        >

          <GigsClaimHeader
            isUpdate={isUpdate}
            showTourFlag={showTourFlag}
            onStartTour={startGigsClaimTour}
          />
          <div className="bg-white shadow-sm rounded-lg p-4 md:px-14 md:py-10">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-[auto_1fr]">
              {/* Left: vertical progress indicator */}
              {/* Repeated mobile progress bar as steps index was incorrect for hidden steps */}
              <div className="md:hidden">
                <WizardProgress
                  steps={claimStepsMobile}
                  currentStepIndex={currentStepIndex}
                  direction="vertical"
                  showStepNumbers={true}
                  showDescriptions={false}
                  showLabels={false}
                />
              </div>
              {/* Desktop-only: vertical stretch circles */}
              <div className="hidden h-full md:block md:pt-8">
                <WizardProgress
                  steps={claimStepsDesktop}
                  currentStepIndex={currentStepIndex}
                  direction="vertical"
                  showStepNumbers={true}
                  showDescriptions={false}
                  showLabels={false}
                  stretch
                />
              </div>

              <div className="flex w-full flex-col gap-6">
                <VenueSection
                  adminRights={adminRights}
                  showManual={showManual}
                  setShowManual={setShowManual}
                />
                {countrySet && (
                  <>
                    <GigDetailsSection
                      setlists={setlists?.data}
                    />
                    {/* 'choose file' and 'no file chosen' placeholders are translated by user's OS, future workaround for custom translated label needed - Matt  */}
                    {sections.includes("evidence") && (
                      <EvidenceSection
                        isUpdate={isUpdate}
                        currentEvidenceURL={form.watch("evidenceURL")}
                        onFileChange={file => handleEvidenceChange(file, "evidenceURL")}
                      />
                    )}

                    <AdmissionSection />

                    {sections.includes("lineUpPosition") && (
                      <LineUpSection
                        variant="radio"
                      />
                    )}
                    {sections.includes("lineUpPositionAlt") && (
                      <LineUpSection
                        variant="combobox"
                      />
                    )}

                    <PromoterSection />

                    {underTwelveEuros && (
                      <SpainProofSection
                        isUpdate={isUpdate}
                        onFileChange={file => handleEvidenceChange(file, "evidenceURL")}
                      />
                    )}

                    {festival && <FestivalSection />}

                    {otherPerformers && <OtherPerformersSection />}

                    <GigsClaimActions
                      isUpdate={isUpdate}
                      adminRights={adminRights}
                      pending={pending}
                      mutationErrorMessage={mutationErrorMessage}
                      onAdd={() => { void addClaim(adminRights); }}
                      onUpdate={() => { void updateClaim(adminRights); }}
                      onDelete={() => { void deleteClaim(adminRights); }}
                    />
                  </>
                )}
              </div>

            </div>
          </div>
        </div>
      </>
    </Form>
  );
}

export default GigsClaim;
